import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { GridviewComponent, GridviewPanel, Orientation } from 'dockview-core';
import 'dockview-core/dist/styles/dockview.css';

// Store for panel content elements (used by the panel class)
const panelContentRegistry = new Map();

/**
 * Custom GridviewPanel implementation for Dash
 * GridviewPanel requires extending the base class and implementing getComponent()
 */
class DashGridviewPanel extends GridviewPanel {
    constructor(id, componentName) {
        super(id, componentName);
        this._element = null;
    }

    getComponent() {
        // Create the content element
        this._element = document.createElement('div');
        this._element.style.height = '100%';
        this._element.style.width = '100%';
        this._element.style.overflow = 'auto';
        this._element.style.boxSizing = 'border-box';
        this._element.className = 'dash-grid-panel-content';
        this._element.setAttribute('data-panel-id', this.id);

        // Store in registry for React to access
        panelContentRegistry.set(this.id, this._element);

        // Notify any listeners that panel is ready (via custom event)
        window.dispatchEvent(new CustomEvent('dashgridpanel-ready', { detail: { id: this.id, element: this._element } }));

        return {
            element: this._element,
            init: (params) => {
                // Panel initialized
            },
            update: (params) => {
                // Handle updates
            },
            dispose: () => {
                panelContentRegistry.delete(this.id);
                window.dispatchEvent(new CustomEvent('dashgridpanel-disposed', { detail: { id: this.id } }));
            }
        };
    }
}

/**
 * DashGridLayout is a component that provides a grid-based resizable panel system.
 * Panels can be arranged in rows and columns with adjustable proportions.
 */
const DashGridLayout = (props) => {
    const {
        id,
        setProps,
        children,
        panels = [],
        layout = null,
        savedLayout = null,
        theme = 'dockview-theme-light',
        height = '600px',
        orientation = 'horizontal',
        proportionalLayout = true,
        hideBorders = false,
        loading_state = null,
    } = props;

    const containerRef = useRef(null);
    const gridviewApiRef = useRef(null);
    const [panelContainers, setPanelContainers] = useState({});
    const [isReady, setIsReady] = useState(false);
    const disposablesRef = useRef([]);

    // Create a mapping of panel IDs to children
    const childrenMap = useMemo(() => {
        const map = {};
        if (children) {
            const childArray = Array.isArray(children) ? children : [children];
            childArray.forEach((child) => {
                if (child && child.props) {
                    const childId = child.props.id ||
                                   child.props._dashprivate_layout?.props?.id ||
                                   child.key;
                    if (childId) {
                        map[childId] = child;
                    }
                }
            });
        }
        return map;
    }, [children]);

    // Initialize gridview
    useEffect(() => {
        if (!containerRef.current) return;

        // Clear registry for this component instance
        panelContentRegistry.clear();

        // Listen for panel ready events
        const handlePanelReady = (e) => {
            const { id: panelId, element } = e.detail;
            setPanelContainers(prev => ({
                ...prev,
                [panelId]: element
            }));
        };

        const handlePanelDisposed = (e) => {
            const { id: panelId } = e.detail;
            setPanelContainers(prev => {
                const next = { ...prev };
                delete next[panelId];
                return next;
            });
        };

        window.addEventListener('dashgridpanel-ready', handlePanelReady);
        window.addEventListener('dashgridpanel-disposed', handlePanelDisposed);

        const gridview = new GridviewComponent(containerRef.current, {
            orientation: orientation === 'vertical' ? Orientation.VERTICAL : Orientation.HORIZONTAL,
            proportionalLayout,
            hideBorders,
            createComponent: (options) => {
                return new DashGridviewPanel(options.id, options.name);
            },
        });

        gridviewApiRef.current = gridview;

        // Setup event listeners
        const layoutDisposable = gridview.onDidLayoutChange(() => {
            if (setProps) {
                try {
                    setProps({ layout: gridview.toJSON() });
                } catch (err) {
                    console.error('Failed to serialize layout:', err);
                }
            }
        });
        disposablesRef.current.push(layoutDisposable);

        // Load initial panels with explicit sizes
        if (panels.length > 0) {
            panels.forEach((panel, index) => {
                const panelId = panel.id || `panel-${index}`;
                gridview.addPanel({
                    id: panelId,
                    component: 'default',
                    size: panel.size || 200,
                    minimumWidth: panel.minimumWidth || 100,
                    maximumWidth: panel.maximumWidth,
                    minimumHeight: panel.minimumHeight || 100,
                    maximumHeight: panel.maximumHeight,
                });
            });
        }

        if (setProps) {
            setProps({ panelCount: gridview.groups.length });
        }

        setIsReady(true);

        return () => {
            window.removeEventListener('dashgridpanel-ready', handlePanelReady);
            window.removeEventListener('dashgridpanel-disposed', handlePanelDisposed);
            disposablesRef.current.forEach(d => d.dispose());
            disposablesRef.current = [];
            setPanelContainers({});
            panelContentRegistry.clear();
            gridview.dispose();
            gridviewApiRef.current = null;
            setIsReady(false);
        };
    }, [theme, orientation, proportionalLayout, hideBorders]);

    // Load saved layout
    useEffect(() => {
        if (gridviewApiRef.current && savedLayout) {
            try {
                gridviewApiRef.current.fromJSON(savedLayout);
            } catch (err) {
                console.error('Failed to load layout:', err);
            }
        }
    }, [savedLayout]);

    // Render portals
    const portals = useMemo(() => {
        if (!isReady) return [];

        return Object.entries(panelContainers).map(([panelId, container]) => {
            if (!container || !document.body.contains(container)) {
                return null;
            }

            const child = childrenMap[panelId];
            if (child) {
                return ReactDOM.createPortal(
                    <div style={{ height: '100%', width: '100%', overflow: 'auto' }} key={panelId}>
                        {child}
                    </div>,
                    container
                );
            }
            return ReactDOM.createPortal(
                <div style={{ padding: '1rem', color: 'var(--dv-foreground-color, #333)' }} key={panelId}>
                    <p>Panel: {panelId}</p>
                </div>,
                container
            );
        }).filter(Boolean);
    }, [panelContainers, childrenMap, isReady]);

    return (
        <div id={id} className={theme} style={{ width: '100%', height }}>
            <div
                ref={containerRef}
                className={theme}
                style={{
                    height: '100%',
                    backgroundColor: 'var(--dv-background-color)',
                    border: '1px solid var(--dv-separator-border)'
                }}
            />
            {portals}
        </div>
    );
};

DashGridLayout.propTypes = {
    id: PropTypes.string.isRequired,
    children: PropTypes.node,
    panels: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        size: PropTypes.number,
        minimumWidth: PropTypes.number,
        maximumWidth: PropTypes.number,
        minimumHeight: PropTypes.number,
        maximumHeight: PropTypes.number,
    })),
    activePanel: PropTypes.string,
    layout: PropTypes.object,
    savedLayout: PropTypes.object,
    theme: PropTypes.string,
    height: PropTypes.string,
    orientation: PropTypes.oneOf(['horizontal', 'vertical']),
    proportionalLayout: PropTypes.bool,
    hideBorders: PropTypes.bool,
    panelCount: PropTypes.number,
    setProps: PropTypes.func,
    loading_state: PropTypes.shape({
        is_loading: PropTypes.bool,
        prop_name: PropTypes.string,
        component_name: PropTypes.string,
    }),
};

export default DashGridLayout;
